import React from 'react';
import Button from '../Button';
import { cn } from '../cn';
import { useTimezone } from '../timezone/TimezoneContext';
import { PortalCashTransactionSummaryGrid } from './PortalCashTransactionSummaryGrid';
import { cashFlowLabel, cashFlowTone, cashPaymentMethodLabel, cashStatusLabel, cashStatusTone, cashToneClassName, } from './cashTransactionPresentation';
import { formatCashDateTime, formatCashUsd, formatCostRateDetail, formatCredits, } from './formatters';
function KvRow({ label, children }) {
    return (React.createElement(React.Fragment, null,
        React.createElement("dt", { className: "text-sm text-muted-foreground" }, label),
        React.createElement("dd", { className: "text-sm font-medium" }, children)));
}
export const PortalCashTransactionDetail = ({ loading, transaction, readOnly = true, onBack, className, }) => {
    var _a, _b, _c;
    const { timezone } = useTimezone();
    if (loading) {
        return React.createElement("p", { className: "p-6 text-sm text-muted-foreground" }, "Loading\u2026");
    }
    if (!transaction) {
        return React.createElement("p", { className: "p-6 text-sm text-muted-foreground" }, "Cash transaction not found.");
    }
    const tx = transaction;
    const performerLabel = ((_a = tx.performedBy) === null || _a === void 0 ? void 0 : _a.name) || ((_b = tx.performedBy) === null || _b === void 0 ? void 0 : _b.email) || tx.performedById || '—';
    return (React.createElement("div", { className: cn('space-y-4 p-6', className) },
        React.createElement("div", { className: "flex flex-wrap items-start justify-between gap-3" },
            React.createElement("div", null,
                React.createElement("h1", { className: "text-2xl font-bold" }, tx.transactionCode),
                React.createElement("p", { className: "mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground" },
                    React.createElement("span", { className: cn('inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold', cashToneClassName[cashFlowTone(tx.flow)]) }, cashFlowLabel(tx.flow)),
                    React.createElement("span", null,
                        tx.sellerName || 'Platform',
                        " \u2192 ",
                        tx.buyerName || tx.buyerId),
                    React.createElement("span", { className: cn('inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold', cashToneClassName[cashStatusTone(tx.status)]) }, cashStatusLabel(tx.status)))),
            onBack ? (React.createElement(Button, { type: "button", variant: "outline", onClick: onBack }, "\u2190 Back to list")) : null),
        readOnly ? (React.createElement("div", { className: "rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" },
            React.createElement("strong", null, "Read-only"),
            " \u2014 Transaction detail is view-only for now.")) : null,
        React.createElement(PortalCashTransactionSummaryGrid, { summary: {
                totalTransactions: 1,
                totalExpected: tx.expectedCash,
                totalReceived: tx.cashReceived,
                paidFullCount: tx.status === 'paid_full' ? 1 : 0,
                pendingPartialCount: tx.status === 'paid_full' ? 0 : 1,
            } }),
        React.createElement("div", { className: "rounded-lg border border-border" },
            React.createElement("div", { className: "border-b border-border px-4 py-3" },
                React.createElement("h2", { className: "text-sm font-semibold" }, "Transaction details")),
            React.createElement("dl", { className: "grid gap-3 p-4 sm:grid-cols-2" },
                React.createElement(KvRow, { label: "Transaction ID" },
                    React.createElement("span", { className: "font-mono" }, tx.transactionCode)),
                React.createElement(KvRow, { label: "Flow" }, cashFlowLabel(tx.flow)),
                React.createElement(KvRow, { label: "Seller" }, tx.sellerName || 'Platform'),
                React.createElement(KvRow, { label: "Buyer" }, tx.buyerName || tx.buyerId),
                React.createElement(KvRow, { label: "Credits sold" },
                    formatCredits(tx.creditAmount),
                    " credits"),
                React.createElement(KvRow, { label: "Cost rate (snapshot)" }, formatCostRateDetail(tx.buyerCostRate)),
                React.createElement(KvRow, { label: "Expected cash" }, formatCashUsd(tx.expectedCash, tx.currency)),
                React.createElement(KvRow, { label: "Cash received" }, formatCashUsd(tx.cashReceived, tx.currency)),
                React.createElement(KvRow, { label: "Payment method" }, cashPaymentMethodLabel(tx.paymentMethod)),
                React.createElement(KvRow, { label: "Received at" }, formatCashDateTime(tx.receivedAt, timezone)),
                React.createElement(KvRow, { label: "Status" },
                    React.createElement("span", { className: cn('inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold', cashToneClassName[cashStatusTone(tx.status)]) }, cashStatusLabel(tx.status))),
                React.createElement(KvRow, { label: "External reference" },
                    React.createElement("span", { className: "font-mono" }, tx.externalReference || '—')),
                React.createElement(KvRow, { label: "Note" }, tx.note || '—'),
                React.createElement(KvRow, { label: "Performed by" }, performerLabel),
                React.createElement(KvRow, { label: "Created at" }, formatCashDateTime(tx.createdAt, timezone)),
                React.createElement(KvRow, { label: "Updated at" }, formatCashDateTime(tx.updatedAt, timezone)))),
        tx.creditTransaction ? (React.createElement("div", { className: "rounded-lg border border-border" },
            React.createElement("div", { className: "border-b border-border px-4 py-3" },
                React.createElement("h2", { className: "text-sm font-semibold" }, "Linked credit transaction"),
                React.createElement("p", { className: "text-xs text-muted-foreground" }, "Wallet credit entry (read-only)")),
            React.createElement("div", { className: "grid gap-4 p-4 sm:grid-cols-3" },
                React.createElement("div", null,
                    React.createElement("p", { className: "text-xs text-muted-foreground" }, "Credit Transaction ID"),
                    React.createElement("p", { className: "mt-1 font-mono text-sm font-semibold" }, tx.creditTransaction.id)),
                React.createElement("div", null,
                    React.createElement("p", { className: "text-xs text-muted-foreground" }, "Wallet"),
                    React.createElement("p", { className: "mt-1 text-sm font-semibold" },
                        tx.buyerName || tx.buyerType,
                        " CDN wallet")),
                React.createElement("div", null,
                    React.createElement("p", { className: "text-xs text-muted-foreground" }, "Balance after"),
                    React.createElement("p", { className: "mt-1 text-sm font-semibold" },
                        formatCredits((_c = tx.creditTransaction.balanceAfter) !== null && _c !== void 0 ? _c : tx.buyerBalanceAfter),
                        " credits"))))) : null,
        tx.events && tx.events.length > 0 ? (React.createElement("div", { className: "rounded-lg border border-border" },
            React.createElement("div", { className: "border-b border-border px-4 py-3" },
                React.createElement("h2", { className: "text-sm font-semibold" }, "Audit log")),
            React.createElement("ul", { className: "space-y-3 p-4" }, tx.events.map((ev) => (React.createElement("li", { key: ev.id, className: "border-l-2 border-green-500 pl-3" },
                React.createElement("p", { className: "text-sm font-semibold" },
                    "Transaction created \u2014 ",
                    cashStatusLabel(tx.status)),
                React.createElement("p", { className: "text-xs text-muted-foreground" },
                    "Credit +",
                    formatCredits(tx.creditAmount),
                    " \u00B7 Cash ",
                    formatCashUsd(tx.cashReceived),
                    " \u00B7",
                    ' ',
                    formatCashDateTime(ev.createdAt, timezone)))))))) : null));
};
